import EffectiveRecordMedia from "./EffectiveRecordMedia";
import ServiceCatalogueFields from "../content/ServiceCatalogueFields";
import { useState } from "react";
import HeroMediaEditor from "./HeroMediaEditor";
import { getDefaultSiteContent } from "lib/siteContentCms";
import { Link, useNavigate } from "react-router-dom";
import { adminRequest } from "lib/adminSiteApi";
import { useAdmin, hasCapability } from "../RequireAdmin";
import {
  ActionForm,
  ErrorNotice,
  Loading,
  Record,
  cleanDraft,
  Fields,
  useRemote,
} from "./Controls";
import { Collection, Lookup } from "./Records";
import Upload from "./Upload";
import { endpoint } from "./catalog";
const BLOCKS = [
  "homeHero",
  "aboutHero",
  "resourcesHero",
  "paperworkHero",
  "servicesHero",
  "webinarsHero",
  "careersHero",
  "contactHero",
  "companiesHero",
  "homeSpeakers",
  "homeMasterclass",
  "homePathways",
  "servicesIntroduction",
  "aboutFocus",
  "careersIntro",
  "partnerReferral",
  "partnerVendor",
  "partnerSpeaker",
  "partnerReseller",
];
export default function Editor({ domain, id, record, onRefresh }) {
  const [target, setTarget] = useState(domain === "site-content" ? "homeHero" : domain === "courses" ? "featured" : domain === "service-catalogue" ? "services" : crypto.randomUUID());
  const index = useRemote(domain === "site-content" ? "/admin/hero-media" : null);
  const needsRecord = !id && ["site-content", "courses", "service-catalogue"].includes(domain);
  const [tick, setTick] = useState(0);
  const selected = id || target;
  const remote = useRemote(needsRecord ? `${endpoint(domain)}/${selected}` : null, tick, { keep: true });
  const choices = [...new Set([...BLOCKS, ...(index.data?.slots || []).map(slot => slot.id)])];
  return <>
    {!id && domain === "site-content" && <label>Content block<select value={selected} onChange={e => setTarget(e.target.value)}>{choices.map(value => <option key={value}>{value}</option>)}</select></label>}
    <Loading state={needsRecord ? remote : { loading: false }}>
      {(!needsRecord || remote.data) && <EditorTarget key={`${domain}:${selected}`} domain={domain} id={id} target={selected}
        record={needsRecord ? remote.data : record} onRefresh={result => { setTick(n => n + 1); onRefresh?.(result); }} />}
    </Loading>
  </>;
}
function EditorTarget({ domain, id, target, record: loadedRecord, onRefresh }) {
  const record = domain === "site-content" && loadedRecord?.missing && !loadedRecord.mediaOnly
    ? { ...getDefaultSiteContent(target), ...loadedRecord } : loadedRecord;
  const profile = useAdmin(), navigate = useNavigate(), schemaState = useRemote(`/admin/editor-schema/${domain}`);
  const canonical = domain === "courses" ? "coursePromotionPoster" : domain === "site-content" && (target.endsWith("Hero") || ["blogLandingHeroMedia", "coursePromotionPoster"].includes(target)) ? (target === "contactHero" ? "homeHero" : target) : null;
  const inherited = domain === "site-content" && target === "contactHero";
  const thumbnailHelp = domain === "courses" ? {
    title: "Hero video thumbnail",
    description: "Choose a public image, save its draft, then publish it for the inline Courses hero video. This uses the existing shared Courses and Welcome video cover.",
  } : {};
  const schema = schemaState.data ? { ...schemaState.data, properties: Object.fromEntries(Object.entries(schemaState.data.properties).filter(([key]) => !canonical || !["imageUrl", "imageAlt", "featuredImageUrl", "featuredImageAlt"].includes(key))) } : null;
  const [selection, setSelection] = useState({}),
    [uploadTick, setUploadTick] = useState(0),
    [versionMode, setVersionMode] = useState("versionId"),
    [selectionError, setSelectionError] = useState(null);
  const artworkFields =
    domain === "services"
      ? ["heroImageUrl", "cardImageUrl"]
      : domain === "site-content"
        ? (canonical ? [] : ["imageUrl"])
        : domain === "courses"
          ? [
              "shortOverviewVideoUrl",
              "fullOverviewVideoUrl",
            ]
          : ["featuredImageUrl"];
  const [artworkField, setArtworkField] = useState(artworkFields[0]);
  const resource = domain === "resources";
  const cap = resource ? "resources.manage" : "content.edit";
  if (!hasCapability(profile, cap))
    return (
      <section className="aw-panel">
        <h2>Record details</h2>
        <Record data={record} />
        {["blogs", "services"].includes(domain) && <EffectiveRecordMedia domain={domain} id={id} revision={record?.revision} />}
        {canonical && <HeroMediaEditor key={canonical} slot={canonical} inherited={inherited} {...thumbnailHelp} />}
        <p>You have read-only access.</p>
      </section>
    );
  return (
    <Loading state={schemaState}>
      {["blogs", "services"].includes(domain) && <EffectiveRecordMedia domain={domain} id={id} revision={record?.revision} />}
      {domain === "courses" && <p className="aw-muted">For an individual course’s theme, open <Link className="aw-link" to="/admin/course-library">Course library</Link>, choose the course, then use Presentation.</p>}
      {schemaState.data && (
        <>
          {canonical && <HeroMediaEditor key={canonical} slot={canonical} inherited={inherited} {...thumbnailHelp} />}
          {!record?.mediaOnly && <ActionForm
            key={`${domain}:${target}`}
            additions={selection}
            renderFields={domain === "service-catalogue" ? (value, setValue) => <ServiceCatalogueFields schema={schema} value={value} setValue={setValue} /> : domain === "services" ? (value, setValue) => <Fields schema={schema} value={value} onChange={setValue} reorder /> : undefined}
            title={record?.revision ? "Edit content" : "Create content"}
            schema={schema}
            initial={{
              status: "draft",
              ...(resource ? { access: "free" } : {}),
              ...cleanDraft(schema, record || {}),
              revision: record?.revision || 0,
            }}
            choices={{
              status: schema.properties.status.enum
                .filter(
                  (v) =>
                    v !== "published" ||
                    resource ||
                    hasCapability(profile, "content.publish"),
                )
                .map((v) => ({ id: v, name: v })),
            }}
            description={
              resource
                ? "Resources are free. Select a ready file version before publishing."
                : domain === "legacy-resources"
                  ? "Edit existing page content here. Add protected files in Resource library; historical file migration remains part of release preparation."
                  : "Published changes show on the website. Search-engine copies of the pages update with the next website release."
            }
            submit={(body, key, baseline) =>
              adminRequest(`${endpoint(domain)}/${target}`, {
                method: "PUT",
                key,
                body: resource
                  ? { ...body, revision: baseline?.revision || 0 }
                  : { revision: baseline?.revision || 0, content: body },
              })
            }
            onDone={(result) => {
              if (!id) navigate(`/admin/${domain}/${target}`);
              else onRefresh(result);
            }}
          />}
          {resource && id && (
            <>
              <Upload
                resourceId={id}
                onDone={() => setUploadTick((n) => n + 1)}
              />
              <section className="aw-panel">
                <h2>Choose a ready file</h2>
                <p>
                  Select a version below, then save the content form. Only a
                  ready PDF can be used for preview.
                </p>
                <label>
                  Use selected version for
                  <select
                    value={versionMode}
                    onChange={(e) => setVersionMode(e.target.value)}
                  >
                    <option value="versionId">Download file</option>
                    <option value="previewVersionId">PDF preview</option>
                  </select>
                </label>
                <button
                  className="aw-button"
                  onClick={() =>
                    setSelection((p) => ({ ...p, previewVersionId: "" }))
                  }
                >
                  Clear preview selection
                </button>
                <ErrorNotice error={selectionError} />
                <Collection
                  domain="versions"
                  params={{ resourceId: id, status: "ready" }}
                  refresh={uploadTick}
                  compact
                  selectRow={(v) => {
                    if (
                      versionMode === "previewVersionId" &&
                      v.mime !== "application/pdf"
                    ) {
                      setSelectionError(
                        new Error("The preview must be a PDF."),
                      );
                      return;
                    }
                    setSelectionError(null);
                    setSelection((p) => ({ ...p, [versionMode]: v.id }));
                  }}
                />
                <Collection
                  domain="versions"
                  params={{ resourceId: id }}
                  refresh={uploadTick}
                  compact
                />
              </section>
            </>
          )}
          {artworkFields.length > 0 && !resource && !["media", "legacy-resources", "service-catalogue"].includes(domain) && (
            <section className="aw-panel">
              <h2>Choose public artwork</h2>
              <p>
                Select a published image or overview video from the media
                library. The selected media fills the artwork fields; save the
                form to apply it.
              </p>
              <label>
                Artwork field
                <select
                  value={artworkField}
                  onChange={(e) => setArtworkField(e.target.value)}
                >
                  {artworkFields.map((field) => (
                    <option key={field} value={field}>
                      {field.replace(/([a-z])([A-Z])/g, "$1 $2")}
                    </option>
                  ))}
                </select>
              </label>
              <ErrorNotice error={selectionError} />
              <Lookup
                domain="media"
                label="Browse media library"
                onSelect={(row) => {
                  const video = artworkField.includes("Video");
                  if (
                    row.status !== "published" ||
                    !row.public ||
                    !(row.contentType || "").startsWith(
                      video ? "video/" : "image/",
                    )
                  ) {
                    setSelectionError(
                      new Error(
                        `Select a published ${video ? "video" : "image"} for this field.`,
                      ),
                    );
                    return;
                  }
                  setSelectionError(null);
                  const altField = {
                    imageUrl: "imageAlt",
                    heroImageUrl: "heroImageAlt",
                    featuredImageUrl: "featuredImageAlt",
                  }[artworkField];
                  setSelection((p) => ({
                    ...p,
                    [artworkField]: row.url,
                    ...(altField ? { [altField]: row.altText || "" } : {}),
                  }));
                }}
              />
            </section>
          )}
        </>
      )}
    </Loading>
  );
}
